import { Button } from '@newcore/ui/buttons';
import { Sizes } from '@newcore/ui/enums/sizes';
import { Text } from '@newcore/ui/typography';
import { useMobileInstallApp } from '@/features/onboarding/hooks/use-mobile-install-app';
import {
  getAppStoreBadgeUrl,
  getPlayStoreBadgeUrl,
} from '@/features/onboarding/utils/store-badges';
import {
  ONBOARDING_DEFAULT_TITLE,
  OnboardingStatusCard,
} from './onboarding-status-card';
import { StoreBadge } from './store-badge';

const PLAY_STORE_BADGE_ALT = 'Get it on Google Play';
const APP_STORE_BADGE_ALT = 'Download on the App Store';

interface MobileInstallAppProps {
  deepLinkUrl: string;
  notice?: string;
}

export function MobileInstallApp({
  deepLinkUrl,
  notice,
}: MobileInstallAppProps) {
  const {
    playStoreUrl,
    appStoreUrl,
    showPlayStore,
    showAppStore,
    handleOpenApp,
  } = useMobileInstallApp({ deepLinkUrl });

  const playStoreBadgeSrc = getPlayStoreBadgeUrl();
  const appStoreBadgeSrc = getAppStoreBadgeUrl();

  return (
    <OnboardingStatusCard
      title={ONBOARDING_DEFAULT_TITLE}
      description='Download the app to complete your account setup.'
    >
      {notice && (
        <Text
          as='p'
          className='text-tertiary text-center'
          data-testid='mobile-install-app-notice'
        >
          {notice}
        </Text>
      )}
      <div
        className='flex flex-col items-center gap-3'
        data-testid='store-badges'
      >
        {showPlayStore && (
          <StoreBadge
            href={playStoreUrl}
            src={playStoreBadgeSrc}
            alt={PLAY_STORE_BADGE_ALT}
            testId='play-store-badge'
          />
        )}
        {showAppStore && (
          <StoreBadge
            href={appStoreUrl}
            src={appStoreBadgeSrc}
            alt={APP_STORE_BADGE_ALT}
            testId='app-store-badge'
          />
        )}
      </div>

      <div className='w-full border-t border-utility-gray-200 pt-4'>
        <Text as='p' className='text-tertiary text-center'>
          Already have the app?
        </Text>
        <Button
          size={Sizes.LG}
          onClick={handleOpenApp}
          data-testid='open-app-button'
          className='mt-3 w-full'
        >
          Open App
        </Button>
      </div>
    </OnboardingStatusCard>
  );
}
